import { LegalType } from "../../types";

export interface AcceptedTerm {
  type: "Candidate" | "Rooster";
  termsAndConditionId: string;
  version: number;
  contentId: string;
  localeCode: string;
}

// LegalType["content"] doesn't (yet) declare `status` locally, so widen via
// assertion to the shape isPublished actually reads at runtime -- mirrors
// TermsAndConditionsPopup's local widening of the same field.
type LegalContent = LegalType["content"][number] & { status?: string };

// Legacy content entries have no `status` field -- treat those as Published
// so pre-existing translations remain acceptable. Kept in sync with
// TermsAndConditionsPopup's `isPublished` predicate.
const isPublished = (c?: { status?: string }) =>
  (c?.status ?? "Published") === "Published";

export const buildAcceptedTerms = (
  legal: LegalType[],
  selectedLanguages: Record<string, string>
): AcceptedTerm[] => {
  return (legal ?? []).reduce<AcceptedTerm[]>((acc, entry) => {
    if (!entry?.content || entry.content.length === 0) {
      return acc;
    }
    const published = (entry.content as LegalContent[]).filter(isPublished);
    if (published.length === 0) {
      // No published content entry exists for this T&C -- don't submit an
      // unacceptable (Draft) contentId, and don't write a silently-broken
      // audit record. Skip the entry entirely.
      return acc;
    }
    const selectedLocale = selectedLanguages?.[entry._id];
    // The selected language only counts if its content entry is published;
    // a Draft selection (or no selection at all) falls back to a published
    // entry, preferring en-US.
    const selected = published.find((c) => c.language === selectedLocale);
    const fallback =
      published.find((c) => c.language === "en-US") ?? published[0];
    const chosen = selected ?? fallback;
    acc.push({
      type:
        entry.type === "Rooster"
          ? ("Rooster" as const)
          : ("Candidate" as const),
      termsAndConditionId: entry._id,
      version: entry.version,
      contentId: chosen._id,
      localeCode: chosen.language
    });
    return acc;
  }, []);
};
